import uniq from 'lodash/uniq'
import groupBy from 'lodash/groupBy'
import range from 'lodash/range'
import { isSolution } from '../actions/index'

export const uprootAction = function (uid) {
    // used to remove an action from the tree
    // when deleting or moving an action
    const action = this.actionsMap[uid]
    if (!action) {
        return
    }
    const parent = this.actionsMap[action.treeParent]
    const prevSibling = this.actionsMap[action.prevSibling]
    const nextSibling = this.actionsMap[action.nextSibling]

    if (prevSibling) {
        prevSibling.nextSibling = action.nextSibling
    }
    if (nextSibling) {
        nextSibling.prevSibling = action.prevSibling
    }

    if (parent) {
        const wasHead = parent.children.includes(uid)
        parent.treeChildren = parent.treeChildren.filter((c) => c !== uid)
        parent.children = parent.children.filter((c) => c !== uid)
        // If we removed the head of the sequence, promote the next sibling
        if (wasHead && nextSibling) {
            parent.children = uniq([...parent.children, nextSibling.uid])
        }
    }

    if (uid === this.topAction) {
        this.topAction = action.nextSibling
        if (this.topAction && this.actionsMap[this.topAction]) {
            this.actionsMap[this.topAction].prevSibling = null
        }
    }

    action.treeParent = null
    action.prevSibling = null
    action.nextSibling = null
}

export const cleanupChildren = function (uid, depth) {
    const action = this.actionsMap[uid]
    if (action) {
        action.treeChildren.forEach((c) => {
            const childAction = this.actionsMap[c]
            if (childAction) {
                if (depth === 0) {
                    childAction.treeParent = null
                }
                childAction.depth = depth
                this.cleanupChildren(c, depth + 1)
            }
        })
    }
}

export const plantAction = function (uid, to, parent) {
    const action = this.actionsMap[uid]
    if (!action) {
        return
    }
    if (!to) {
        let toAction = this.actionsMap[this.topAction]
        if (toAction) {
            // redo links from removing action in the tree
            if (action.prevSibling) {
                const prevSiblingAction = this.actionsMap[action.prevSibling]
                if (prevSiblingAction) {
                    prevSiblingAction.nextSibling = action.nextSibling
                }
            }
            if (action.nextSibling) {
                const nextSiblingAction = this.actionsMap[action.nextSibling]
                if (nextSiblingAction) {
                    nextSiblingAction.prevSibling = action.prevSibling
                }
            }

            // Link action to top action
            action.depth = 0
            action.treeParent = null
            action.prevSibling = null
            action.nextSibling = this.topAction
            toAction.prevSibling = uid

            // Update top action
            this.topAction = uid
        }
    } else if (parent) {
        let toAction = this.actionsMap[to]
        if (toAction) {
            // make sure we don't change the top if needed
            if (uid === this.topAction && action.nextSibling) {
                this.topAction = action.nextSibling
                const topActionFromMap = this.actionsMap[this.topAction]
                if (topActionFromMap) {
                    topActionFromMap.prevSibling = null
                }
                action.nextSibling = null
            }

            // move the action as a child of the to action
            toAction.children = uniq([...toAction.children, uid])
            toAction.treeChildren = uniq([...toAction.treeChildren, uid])
            action.treeParent = to
            action.depth = toAction.depth + 1
            action.visible = toAction.expanded && toAction.visible
        }
    } else {
        let toAction = this.actionsMap[to]
        if (toAction) {
            // make sure we create correctly new top
            if (uid === this.topAction && action.nextSibling) {
                this.topAction = action.nextSibling
                const topActionFromMap = this.actionsMap[this.topAction]
                if (topActionFromMap) {
                    topActionFromMap.prevSibling = null
                }
                action.depth = 0
            }
            // if parent is false it means the to will be a prevSibling
            action.nextSibling = toAction.nextSibling
            if (toAction.nextSibling) {
                const nextSiblingToAction = this.actionsMap[toAction.nextSibling]
                if (nextSiblingToAction) {
                    nextSiblingToAction.prevSibling = uid
                }
            }
            toAction.nextSibling = uid
            action.prevSibling = to
            action.treeParent = toAction.treeParent
            if (action.treeParent) {
                const treeParentAction = this.actionsMap[action.treeParent]
                if (treeParentAction) {
                    treeParentAction.treeChildren = uniq([
                        ...treeParentAction.treeChildren,
                        uid,
                    ])
                }
            }
            action.depth = toAction.depth
        }
    }
    this.cleanupChildren(uid, action.depth + 1)
    action.selected = false
}

/**
 * Ability to move an action in the UI
 */
export const moveAction = function ({ uid, toAction, parent }) {
    try {
        const actionToMove = this.actionsMap[uid]

        // Check if action is already in the target position
        if (actionToMove) {
            // If parent is true, check if already under the same parent
            if (parent && actionToMove.treeParent === toAction) {
                return this.validateRunbook()
            }
            // If parent is false, check if already positioned after toAction
            if (!parent && actionToMove.prevSibling === toAction) {
                return this.validateRunbook()
            }
        }

        this.uprootAction(uid)
        this.plantAction(uid, toAction, parent)
    } catch (e) {
        return {
            success: false,
            message: this.CIRCULAR_TREE_MESSAGE,
        }
    }
    return this.validateRunbook()
}

/**
 * Ability to move multiple actions in the UI
 */
export const moveActions = function (
    ids,
    { to, parent, firstChild, sortAlphabetically }
) {
    if (!sortAlphabetically) {
        ids = ids.sort((a, b) => {
            let a1 = this.actionsMap[a]
            let b2 = this.actionsMap[b]
            if (a1 && b2) {
                if (a1.uid < b2.uid) { return -1 }
                if (a1.uid > b2.uid) { return 1 }
            }
            return 0
        })
    } else {
        ids = ids.sort((a, b) => {
            let a1 = this.actionsMap[a]
            let b2 = this.actionsMap[b]
            if (a1 && b2) {
                const name1 = a1.name.toLowerCase()
                const name2 = b2.name.toLowerCase()
                if (name1 < name2) { return -1 }
                if (name1 > name2) { return 1 }
            }
            return 0
        })
    }
    if (!parent) {
        for (let i = 0; i < ids.length; i++) {
            const { success, message } = this.moveAction({
                uid: ids[i],
                toAction: to,
                parent: false,
            })
            if (!success) {
                return {
                    success,
                    message: `Cannot perform all the moves: ${message}`,
                }
            }
            to = ids[i]
        }
    } else if (parent) {
        if (firstChild) {
            let prevSibling = ids[0]
            const { success, message } = this.moveAction({
                uid: ids[0],
                toAction: to,
                parent: true,
            })
            if (!success) {
                return {
                    success,
                    message: `Cannot perform all the moves: ${message}`,
                }
            }
            for (let i = 1; i < ids.length; i++) {
                const { success, message } = this.moveAction({
                    uid: ids[i],
                    toAction: prevSibling,
                    parent: false,
                })
                prevSibling = ids[i]
                if (!success)
                    return {
                        success,
                        message: `Cannot perform all the moves: ${message}`,
                    }
            }
        } else {
            for (let i = 0; i < ids.length; i++) {
                const { success, message } = this.moveAction({
                    uid: ids[i],
                    toAction: to,
                    parent: true,
                })
                if (!success) {
                    return {
                        success: false,
                        message: `Cannot perform all the moves: ${message}`,
                    }
                }
            }
        }
    } else {
        return { success: false, message: 'No sibling or parent specified' }
    }
    return { success: true, message: this.VALID_TREE_MESSAGE }
}

/**
 * Ability to delete an action in the UI
 */
export const deleteAction = function (uid, withChildren) {
    let removedUids = []
    uid = parseInt(uid, 10)
    const actionToDelete = this.actionsMap[uid]
    if (!actionToDelete) {
        return removedUids
    }

    if (withChildren) {
        actionToDelete.treeChildren.forEach(
            (c) =>
                (removedUids = removedUids.concat(this.deleteAction(c, true)))
        )
    } else {
        this.cleanupChildren(uid, 0)
    }
    this.uprootAction(uid)
    if (this.topAction === uid && actionToDelete.nextSibling) {
        const nextSiblingId = actionToDelete.nextSibling
        const nextSiblingAction = this.actionsMap[nextSiblingId]
        if (nextSiblingAction) {
            this.topAction = nextSiblingId
            nextSiblingAction.prevSibling = null
        }
    }
    if (actionToDelete.device) {
        const ciRecord = this.ciToUid[actionToDelete.device]
        if (ciRecord) {
            delete ciRecord.uids[uid]
            if (
                Object.keys(ciRecord.uids)
                    .length === 0
            ) {
                delete this.ciToUid[actionToDelete.device]
            }
        }
    }
    removedUids.push(uid)
    delete this.actionsMap[uid]
    return removedUids
}

/**
 * Apply the UI filtering query to the actions. These are the filters you see in the UI in the right side
 * example: filter on 'owned by me', 'Select status', 'Search by owner', etc...
 * This method filters actions on the filteredByUser field
 * @param query
 * @param action
 * @returns {Boolean}
 */
export const applyQueryToAction = function (query, action) {
    let filteredByUser = false
    const actionParentFromMap = this.actionsMap[action.treeParent] || {}
    const queryKeys = Object.keys(query)
    const twoOwners =
        queryKeys.includes('ownedByMeFilter') &&
        queryKeys.includes('ownedByMyTeamFilter')

    if (twoOwners) {
        const value = query['ownedByMeFilter']
            filteredByUser = !(
                action.ownerUserId === value.id ||
            value.teams.includes(action.ownerTeamId)
            )
    }
    queryKeys.forEach((key) => {
        if (filteredByUser) {
            return
        }

        let value = query[key]
        if (typeof value === 'string') {
            value = value.toLowerCase()
        }

        switch (key) {
            case 'statusesFilter':
                const statusParentMatch = value?.includes(
                    actionParentFromMap?.status
                )
                const statusCurrentMatch = value?.includes(action?.status)

                if (statusCurrentMatch) {
                    action.filteredAction = true
                }

                filteredByUser = !(statusCurrentMatch || statusParentMatch)
                break
            case 'ownedByMeFilter':
                if (!twoOwners && value) {
                    const ownedByMeCurrentMatch =
                        action?.ownerUserId === value.id
                    const ownedByMeParentMatch =
                        actionParentFromMap?.ownerUserId === value.id

                    if (ownedByMeCurrentMatch) {
                        action.filteredAction = true
                    }

                    filteredByUser = !(
                        ownedByMeCurrentMatch || ownedByMeParentMatch
                    )
                }
                break
            case 'ownedByMyTeamFilter':
                if (!twoOwners && value) {
                    const ownedByMyTeamCurrentMatch = value.teams?.includes(
                        action?.ownerTeamId
                    )
                    const ownedByMyTeamParentMatch = value.teams?.includes(
                        actionParentFromMap?.ownerTeamId
                    )

                    if (ownedByMyTeamCurrentMatch) {
                        action.filteredAction = true
                    }

                    filteredByUser = !(
                        ownedByMyTeamCurrentMatch || ownedByMyTeamParentMatch
                    )
                }
                break
            case 'simplifiedView':

                // Remove Solution with no children
                if (
                    isSolution(action) &&
                    (!Array.isArray(action.treeChildren) || action.treeChildren.length === 0)
                ) {
                    filteredByUser = true;
                    return;
                }

                // Hide nested Solution nodes, only show the first in the chain
                if (isSolution(action)) {
                    let parent = this.actionsMap[action.treeParent];
                    // If any ancestor is also a Solution, filter this one
                    while (parent) {
                        if (isSolution(parent)) {
                            filteredByUser = true;
                            return;
                        }
                        parent = this.actionsMap[parent.treeParent];
                    }

                    return;
                }

                // Filter out actions that have a Solution as parent
                let parent = this.actionsMap[action.treeParent];
                if (parent && isSolution(parent)) {
                    filteredByUser = true;
                    return;
                }

                // For all other actions, show them (do not filter)
                return;
            default:
                break
        }
    })
    return filteredByUser
}

/**
 * Applies the applyQueryToAction method to every action to see if the action should be filtered out
 * This method filters actions on the filteredByUser field
 * @param query
 * @returns {Array}
 */
export const applyUserFilters = function (query) {
    let depthGroups = groupBy(this.actionsMap, (a) => a.depth)
    const isSimplifiedView = !!query.simplifiedView;
    for (let level of range(0, this.runbook.maxDepth + 1)) {
        let grp = depthGroups[level]
        grp.forEach((action) => {
            const actionFromMap = this.actionsMap[action.uid]
            if (actionFromMap) {
                actionFromMap.filteredAction = false

                const filteredByUser = this.applyQueryToAction(query, actionFromMap)

                actionFromMap.filteredByUser = filteredByUser

                // Expand and unfilter all parent actions of a visible, unfiltered action,
                // but only if:
                // - the view is not in simplified mode,
                // - the action is not filtered by
                if (!isSimplifiedView && !filteredByUser && !actionFromMap.filteredByAction) {
                    const parentUID = actionFromMap.treeParent
                    if (parentUID) {
                        let currentParentAction = this.actionsMap[parentUID]
                        // Expanding and showing the parent actions of the filtered result.
                        while (currentParentAction) {
                            currentParentAction.expanded = true
                            currentParentAction.filteredByUser = false
                            const grandParentUID = currentParentAction.treeParent
                            currentParentAction = grandParentUID ? this.actionsMap[grandParentUID] : null
                        }
                    }
                }
            }
        })
    }

    return this.actionsMap
}
